"use client";

import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight } from "lucide-react";
const experiences = [{
  name: "Support Automation",
  duration: "INTEGRATION",
  description: "Deploy intelligent AI agents that resolve customer inquiries instantly, 24/7, without human intervention.",
  price: "SCALABLE SOLUTIONS",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/support_automation.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/support_automation.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/support_automation.png-wvc-sizes",
  alt: "AI customer support interface"
}, {
  name: "Booking Systems",
  duration: "WORKFLOW",
  description: "Automate your appointment scheduling with smart calendars that sync directly to your operational dashboard.",
  price: "SEAMLESS SYNC",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/booking_systems.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/booking_systems.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/booking_systems.webp-wvc-sizes",
  alt: "Automated booking calendar interface"
}, {
  name: "Invoicing Logic",
  duration: "FINANCIAL",
  description: "Eliminate manual billing errors with automated invoicing triggers that track payments and send reminders.",
  price: "PRECISION BILLING",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/invoicing_logic.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/invoicing_logic.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/invoicing_logic.webp-wvc-sizes",
  alt: "Automated financial invoicing dashboard"
}, {
  name: "Email Marketing",
  duration: "ENGAGEMENT",
  description: "Personalized outreach sequences triggered by user behavior, ensuring your message hits at the perfect moment.",
  price: "HIGH CONVERSION",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/email_marketing.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/email_marketing.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/email_marketing.png-wvc-sizes",
  alt: "Automated email marketing campaign flow"
}, {
  name: "AI Chatbots",
  duration: "INTERFACE",
  description: "Custom-trained conversational agents that understand your brand voice and handle complex lead qualification.",
  price: "24/7 AVAILABILITY",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_chatbots.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_chatbots.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_chatbots.webp-wvc-sizes",
  alt: "AI chatbot interface design"
}, {
  name: "Workflow Integration",
  duration: "OPERATIONS",
  description: "Connect your disparate software tools into one unified, automated ecosystem that eliminates data silos.",
  price: "TOTAL EFFICIENCY",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/workflow_integration.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/workflow_integration.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/workflow_integration.png-wvc-sizes",
  alt: "Workflow integration diagram"
}];
export default function Ecosystem() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="92" id="our-automation-ecosystem" className="bg-background text-foreground py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div ref={introRef} className={`max-w-3xl mb-20 md:mb-28 transition-all duration-1000 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-4 mb-8">
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-foreground/70">
              § 03 / Automation Ecosystem
            </span>
            <span className="h-px flex-1 max-w-24 bg-foreground/30" />
          </div>
          <h2 className="font-default font-semibold text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground">
            <span className="text-foreground/70">Architecting</span>
            <br />
            <span>autonomous operations.</span>
          </h2>
          <p className="mt-8 max-w-xl font-default text-base md:text-lg leading-relaxed text-muted-foreground">
            We build high-precision digital infrastructure that transforms manual bottlenecks into 
            self-sustaining automated workflows for modern enterprises.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 md:gap-y-20">
          {experiences.map((exp, i) => <ExperienceCard key={exp.name} experience={exp} index={i} />)}
        </div>
      </div>
    </section>;
}
type Experience = {
  name: string;
  duration: string;
  description: string;
  price: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
function ExperienceCard({
  experience,
  index
}: {
  experience: Experience;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${index % 3 * 100}ms`
  }} className={`group flex flex-col transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="relative overflow-hidden rounded-lg transition-transform duration-500 ease-out group-hover:-translate-y-1">
        <div className="relative aspect-[3/4] overflow-hidden rounded-lg">
          <img src={experience.image} data-wvc-srcset={experience.srcset} data-wvc-sizes={experience.sizes} alt={experience.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-foreground/5 pointer-events-none" />
        </div>

        <span className="pointer-events-none absolute top-3 left-3 h-5 w-5 border-l border-t border-foreground/70 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <span className="pointer-events-none absolute top-3 right-3 h-5 w-5 border-r border-t border-foreground/70 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <span className="pointer-events-none absolute bottom-3 left-3 h-5 w-5 border-l border-b border-foreground/70 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <span className="pointer-events-none absolute bottom-3 right-3 h-5 w-5 border-r border-b border-foreground/70 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      </div>

      <div className="pt-6 flex flex-col">
        <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-foreground/70">
          {experience.duration}
        </span>
        <h3 className="mt-3 font-default font-semibold text-2xl md:text-[1.75rem] leading-tight tracking-tight text-foreground">
          {experience.name}
        </h3>
        <p className="mt-3 font-default text-base leading-relaxed text-muted-foreground">
          {experience.description}
        </p>
        <span className="mt-5 font-mono text-[11px] tracking-[0.25em] uppercase text-foreground/70">
          {experience.price}
        </span>

        <div className="mt-4">
          <Button asChild variant="link" className="h-auto p-0 text-foreground hover:text-foreground/70 hover:no-underline font-default text-sm tracking-wide group/btn">
            <Link to="/ConnectPage" aria-label={`Inquire about ${experience.name}`}>
              <span className="relative inline-flex items-center gap-2 pb-1 border-b border-foreground/50 transition-colors duration-300 group-hover/btn:border-foreground/70">
                Inquire
                <ArrowRight className="size-3.5 transition-transform duration-300 group-hover/btn:translate-x-1" />
              </span>
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}